/**
 * Action Items page.
 *
 * Pre-migration-0081 this page fetched 4 separate data sources (alerts,
 * remediation_tasks, ai_insights, signing_domains per domain) and
 * merged + deduped them client-side via regex on item titles. All gone
 * — the unified `action_items` table is fetched in a single call from
 * `/api/action-items`, dedup happens at write time via fingerprint, and
 * signing-domain items are real persistent rows materialized by the
 * cron-worker.
 */

import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Shield,
  ChevronDown,
  ChevronRight,
  Ban,
  Trash2,
  Crown,
  Lightbulb,
  Wrench,
} from "lucide-react";
import { api, AIInsightSuppression } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useAuthStore } from "@/stores/auth";
import {
  PRIORITY_ORDER,
  SOURCE_BADGE,
  type UnifiedItem,
} from "@/components/action-items/shared";
import {
  ActionItemCard,
  type ActionItemAction,
} from "@/components/action-items/ActionItemCard";
import { useFeatureDiscovery } from "@/lib/ux-analytics";

type StatusFilter = "active" | "completed";

/**
 * Narrow the list to one kind of item, from `?kind=` in the URL.
 *
 * Three live places link here meaning "show me the tasks" — the
 * `/remediation` redirect that replaced the deleted remediation page, the
 * "Remediation" command-palette entry in `AppShell`, and the getting-started
 * checklist on the dashboard. (A fourth, `components/sources/
 * TaskGenerationLink.tsx`, carries the same link but is itself unimported;
 * it is updated to match rather than left inconsistent, and is on the dead-
 * component list in doc 61.)
 *
 * All of them passed `?tab=tasks`, which this page never read: it reads
 * `?filter=`, whose only values are `active` and `completed`. So each one
 * landed on the undifferentiated list of everything and the param was
 * decoration.
 *
 * The data was always there — `kind` is fetched and already rendered as a
 * badge on every row — so this honours the intent those links were written
 * with rather than deleting the param and quietly making them mean less.
 * `standing` is deliberately absent: those rows are fetched separately and
 * are not a thing anyone navigates to by kind.
 */
type KindFilter = "alert" | "task" | "insight";

const KIND_FILTERS: readonly KindFilter[] = ["alert", "task", "insight"];

function parseKindFilter(raw: string | null): KindFilter | null {
  return KIND_FILTERS.includes(raw as KindFilter) ? (raw as KindFilter) : null;
}

export function Alerts() {
  useFeatureDiscovery("alerts");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    searchParams.get("filter") === "completed" ? "completed" : "active"
  );
  const [kindFilter, setKindFilter] = useState<KindFilter | null>(() =>
    parseKindFilter(searchParams.get("kind"))
  );
  const [showCompleted, setShowCompleted] = useState(false);
  const { activeClientOrg, organization } = useAuthStore();
  const currentTier = organization?.billing_tier || "free";
  const isFreeTier = currentTier === "free";

  // One unified state slice replaces the previous five (alerts,
  // remediationTasks, insights, signingDomainItems, plus their loaders).
  const [items, setItems] = useState<UnifiedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [suppressions, setSuppressions] = useState<AIInsightSuppression[]>([]);

  // Deep link: ?id= scrolls to and highlights a specific item
  const deepLinkId = searchParams.get("id");
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const deepLinkHandled = useRef(false);

  // Fetch the unified feed. Pulls `active` + `completed` + `snoozed` in
  // one request so both tabs render off the same state. Scoped to
  // event kinds only (alert / task / insight) — per-source standing
  // rows now live exclusively on the Sources Needs Work bucket so
  // surfacing them here would duplicate the same row across two
  // surfaces. Cross-source items (provider tasks, domain posture,
  // policy events) are what this page is now reserved for.
  async function refresh() {
    try {
      const data = await api.getActionItems({
        status: "active,completed,snoozed",
        kind: "alert,task,insight",
        limit: 500,
      });
      setItems(data.items);
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadSuppressions() {
    try {
      const data = await api.getInsightSuppressions();
      setSuppressions(data.suppressions || []);
    } catch (error) {
      console.error("Failed to load suppressions:", error);
    }
  }

  useEffect(() => {
    setLoading(true);
    refresh();
    loadSuppressions();

    const handleClientChange = () => refresh();
    window.addEventListener("msp-client-changed", handleClientChange);
    return () => window.removeEventListener("msp-client-changed", handleClientChange);
  }, [activeClientOrg?.id]);

  // Single status-transition handler replaces the prior six (resolveAlert,
  // completeTask, skipTask, snoozeInsight, resolveInsight, suppressInsight).
  async function handleItemAction(itemId: string, action: ActionItemAction) {
    // Optimistic update — drop the item from active immediately so the
    // user sees the click respond. The refresh below corrects state if
    // the server disagrees.
    setItems((prev) =>
      prev.map((i) =>
        i.id === itemId
          ? { ...i, status: action === "snooze" ? "snoozed" : action === "complete" ? "completed" : action === "dismiss" ? "dismissed" : "not_applicable" }
          : i
      )
    );

    try {
      await api.updateActionItem(itemId, { status: actionToStatus(action) });
      if (action === "not_applicable") {
        // Reload suppressions to show the new entry.
        await loadSuppressions();
      }
    } catch (error) {
      console.error(`Failed to ${action} item:`, error);
      toast.error(`Failed to ${action} item`);
      // Revert by reloading from server.
      refresh();
    }
  }

  async function handleDeleteSuppression(suppressionId: string) {
    try {
      await api.deleteInsightSuppression(suppressionId);
      setSuppressions((prev) => prev.filter((s) => s.id !== suppressionId));
    } catch (error) {
      console.error("Failed to delete suppression:", error);
      toast.error("Failed to remove suppression");
    }
  }

  // Applied before the active/completed split so both tabs and every count
  // agree with what is on screen. Filtering at render instead would leave the
  // severity counts describing rows the user cannot see.
  const kindScopedItems = useMemo(
    () => (kindFilter ? items.filter((i) => i.kind === kindFilter) : items),
    [items, kindFilter]
  );

  const activeItems = useMemo(
    () =>
      kindScopedItems
        .filter((i) => i.status === "active" || i.status === "snoozed")
        .sort((a, b) =>
          (PRIORITY_ORDER[a.severity] ?? 5) -
          (PRIORITY_ORDER[b.severity] ?? 5)
        ),
    [kindScopedItems]
  );

  const completedItems = useMemo(
    () =>
      kindScopedItems
        .filter(
          (i) =>
            i.status === "completed" ||
            i.status === "dismissed" ||
            i.status === "not_applicable"
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
    [kindScopedItems]
  );

  const counts = useMemo(
    () => ({
      critical: activeItems.filter((i) => i.severity === "critical").length,
      high: activeItems.filter((i) => i.severity === "high").length,
      medium: activeItems.filter((i) => i.severity === "medium").length,
      completed: completedItems.length,
    }),
    [activeItems, completedItems]
  );

  // Scroll to and highlight the deep-linked item once data loads
  useEffect(() => {
    if (!deepLinkId || deepLinkHandled.current || items.length === 0) return;

    const target = items.find((i) => i.id === deepLinkId);
    const targetIsCompleted =
      target &&
      (target.status === "completed" ||
        target.status === "dismissed" ||
        target.status === "not_applicable");
    if (targetIsCompleted && statusFilter !== "completed") {
      handleFilterChange("completed");
    }

    const el = document.getElementById(`action-item-${deepLinkId}`);
    if (el) {
      deepLinkHandled.current = true;
      setHighlightedId(deepLinkId);
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      setTimeout(() => setHighlightedId(null), 2100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId, items, statusFilter]);

  function clearKindFilter() {
    setKindFilter(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("kind");
    setSearchParams(newParams, { replace: true });
  }

  function handleFilterChange(filter: StatusFilter) {
    setStatusFilter(filter);
    const newParams = new URLSearchParams(searchParams);
    if (filter === "completed") {
      newParams.set("filter", "completed");
    } else {
      newParams.delete("filter");
    }
    setSearchParams(newParams, { replace: true });
  }

  if (loading && items.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Action Items</h1>
          <p className="text-slate-500">Your prioritized email security actions.</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Action Items</h1>
        <p className="text-slate-500">Your prioritized email security actions.</p>
      </div>

      {/* A filter with nothing on screen saying so is its own defect: the user
          arrives from the "Remediation" nav entry, sees a short list, and has
          no way to tell it is filtered rather than empty. The counts above
          follow this filter too, so the chip explains those as well. */}
      {kindFilter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Showing only</span>
          <span
            className={`px-2 py-0.5 rounded font-medium ${SOURCE_BADGE[kindFilter].className}`}
          >
            {SOURCE_BADGE[kindFilter].label}
          </span>
          <button
            type="button"
            onClick={clearKindFilter}
            className="text-slate-500 underline underline-offset-2 hover:text-slate-800 focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          >
            Show all items
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard icon={AlertTriangle} label="Critical" value={counts.critical} color="red" />
        <SummaryCard icon={Shield} label="High" value={counts.high} color="amber" />
        <SummaryCard icon={Clock} label="Medium" value={counts.medium} color="blue" />
        <SummaryCard icon={CheckCircle2} label="Completed" value={counts.completed} color="green" />
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => handleFilterChange("active")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === "active"
              ? "bg-slate-800 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          Active ({activeItems.length})
        </button>
        <button
          onClick={() => handleFilterChange("completed")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === "completed"
              ? "bg-slate-800 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          Completed ({completedItems.length})
        </button>
      </div>

      {statusFilter === "active" && (
        <div className="space-y-3">
          {activeItems.length === 0 ? (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">All clear</h3>
                <p className="text-slate-500">
                  No active action items. Your email security is looking good.
                </p>
              </div>

              {isFreeTier && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-slate-800 mb-1">
                        See What You Should Fix Next
                      </h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Paid plans include AI-powered insights that analyze your DMARC
                        data and tell you exactly which senders to fix, plus guided
                        remediation tasks that walk you through each step.
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3 mb-4">
                        <div className="flex items-start gap-2">
                          <Wrench className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">Guided Remediation</p>
                            <p className="text-xs text-slate-500">Step-by-step tasks to fix failing senders</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">AI Insights</p>
                            <p className="text-xs text-slate-500">Proactive analysis of your authentication gaps</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <a
                          href="/settings/billing"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors text-sm"
                        >
                          <Crown className="w-4 h-4" />
                          Start Free Trial
                        </a>
                        <span className="text-xs text-slate-400">
                          14-day free trial, cancel anytime
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            activeItems.map((item) => (
              <ActionItemCard
                key={item.id}
                item={item}
                id={`action-item-${item.id}`}
                highlighted={highlightedId === item.id}
                onNavigate={navigate}
                onAction={handleItemAction}
              />
            ))
          )}

          {completedItems.length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
              >
                {showCompleted ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                Completed ({completedItems.length})
              </button>
              {showCompleted && (
                <div className="space-y-2 mt-3">
                  {completedItems.slice(0, 20).map((item) => (
                    <div
                      key={item.id}
                      className="bg-slate-50 rounded-lg border border-slate-200 p-3 opacity-60"
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-slate-600 line-through">
                          {item.title}
                        </span>
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-medium ${SOURCE_BADGE[item.kind].className}`}
                        >
                          {SOURCE_BADGE[item.kind].label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {statusFilter === "completed" && (
        <div className="space-y-3">
          {completedItems.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                No completed items yet
              </h3>
              <p className="text-slate-500">Completed actions will appear here.</p>
            </div>
          ) : (
            completedItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl border border-slate-200 p-4 opacity-75"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-slate-700">{item.title}</span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${SOURCE_BADGE[item.kind].className}`}
                      >
                        {SOURCE_BADGE[item.kind].label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{item.description}</p>
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {suppressions.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
            <Ban className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-700">
              Not Applicable Suppressions
            </h3>
            <span className="ml-auto text-xs text-slate-400">
              These items will not resurface for the specified context. Remove to re-enable.
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {suppressions.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">
                    {s.insightType.replace(/_/g, " ")}
                    {s.fingerprint && (
                      <span className="ml-2 text-slate-500">— {s.fingerprint}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400">
                    {s.domainName ?? "All domains"} · Suppressed{" "}
                    {new Date(s.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteSuppression(s.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  title="Remove suppression"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function actionToStatus(action: ActionItemAction): "snoozed" | "completed" | "dismissed" | "not_applicable" {
  switch (action) {
    case "snooze":
      return "snoozed";
    case "complete":
      return "completed";
    case "dismiss":
      return "dismissed";
    case "not_applicable":
      return "not_applicable";
  }
}

// ============================================================================
// Summary Card
// ============================================================================

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  color: "red" | "amber" | "blue" | "green";
}) {
  const colorClasses = {
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    blue: "bg-blue-50 text-blue-500",
    green: "bg-emerald-50 text-emerald-700",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}
